import { Form, Input, Checkbox } from 'antd';
import { Controller } from 'react-hook-form';
import MFAInput from '../MFAInput';
import type { ControlledFormItemProps } from './types';

/**
 * ControlledFormItem Component
 *
 * A reusable component that combines Ant Design's Form.Item with React Hook Form's Controller
 * and the input components. Supports different input types with their specific APIs.
 *
 * @example Text Input
 * ```tsx
 * <ControlledFormItem
 *   name="email"
 *   control={control}
 *   errors={errors}
 *   label={t('Email')}
 *   inputType="email"
 *   inputProps={{
 *     prefix: <UserOutlined />,
 *     placeholder: t('your.email@example.com'),
 *     autoComplete: 'email'
 *   }}
 * />
 * ```
 *
 * @example Password Input
 * ```tsx
 * <ControlledFormItem
 *   name="password"
 *   control={control}
 *   errors={errors}
 *   label={t('Password')}
 *   inputType="password"
 *   inputProps={{
 *     prefix: <LockOutlined />,
 *     placeholder: t('Enter your password'),
 *     autoComplete: 'current-password'
 *   }}
 * />
 * ```
 *
 * @example Checkbox
 * ```tsx
 * <ControlledFormItem
 *   name="rememberMe"
 *   control={control}
 *   errors={errors}
 *   inputType="checkbox"
 *   checkboxLabel={t('Remember me')}
 * />
 * ```
 *
 * @example MFA Input
 * ```tsx
 * <ControlledFormItem
 *   name="code"
 *   control={control}
 *   errors={errors}
 *   label={t('Verification Code')}
 *   inputType="mfa"
 *   inputProps={{
 *     length: 6,
 *     error: !!errors.code,
 *     disabled: loading,
 *     onComplete: (code) => handleVerify(code)
 *   }}
 * />
 * ```
 *
 * @example Custom Component (backwards compatibility)
 * ```tsx
 * <ControlledFormItem
 *   name="custom"
 *   control={control}
 *   errors={errors}
 *   label={t('Custom')}
 * >
 *   {(field) => <CustomInput {...field} />}
 * </ControlledFormItem>
 * ```
 */
const ControlledFormItem = <
  TFieldValues extends Record<string, unknown> = Record<string, unknown>,
>(
  props: ControlledFormItemProps<TFieldValues>
) => {
  const { name, control, errors, label, formItemProps = {} } = props;

  const fieldError = errors?.[name] as { message?: string } | undefined;
  const inputId = `${name}-input`;

  const renderInput = (field: {
    onChange: (value: unknown) => void;
    onBlur: () => void;
    value: unknown;
    name: string;
  }) => {
    // Check if it's a custom component (has children)
    if ('children' in props) {
      return props.children(field);
    }

    // Handle built-in input types
    const { inputType } = props;

    switch (inputType) {
      case 'text':
      case 'email':
        return (
          <Input
            {...(props.inputProps || {})}
            id={inputId}
            value={field.value as string}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            name={field.name}
            type={inputType}
          />
        );

      case 'password':
        return (
          <Input.Password
            {...(props.inputProps || {})}
            id={inputId}
            value={field.value as string}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            name={field.name}
            type={inputType}
          />
        );

      case 'checkbox':
        return (
          <Checkbox
            {...(props.inputProps || {})}
            id={inputId}
            checked={field.value as boolean}
            onChange={(e) => field.onChange(e.target.checked)}
          >
            {props.checkboxLabel}
          </Checkbox>
        );

      case 'mfa':
        return (
          <MFAInput
            {...(props.inputProps || {})}
            value={field.value as string}
            onChange={field.onChange}
          />
        );

      default:
        return (
          <Input
            id={inputId}
            value={field.value as string}
            onChange={(e) => field.onChange(e.target.value)}
          />
        );
    }
  };

  return (
    <Form.Item
      label={label}
      htmlFor={inputId}
      validateStatus={fieldError ? 'error' : ''}
      help={fieldError?.message}
      {...formItemProps}
    >
      <Controller
        name={name as never}
        control={control}
        render={({ field }) => renderInput(field)}
      />
    </Form.Item>
  );
};

export default ControlledFormItem;
